import React, { useEffect, useState, useCallback } from "react";
import { LogIn, CalendarDays, Package, IndianRupee, LogOut, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BalancePay from "@/components/BalancePay";

const FLOW = ["booked", "confirmed", "in_progress", "completed"];

export default function Account() {
  const { user, loading, logout } = useAuth();
  const { t } = useLang();
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (user) api.get("/my/bookings").then(({ data }) => setBookings(data)).catch(() => {});
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const login = () => {
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const cancel = async (bn) => {
    if (!window.confirm("Cancel this booking? 75% of paid advance is refundable.")) return;
    setBusy(true);
    try { const { data } = await api.post(`/my/bookings/${bn}/cancel`); toast.success(`Cancelled. Refund: ${fmtINR(data.refund_amount)}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-gold">Loading…</div>;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-24 text-center">
        <h1 className="font-display text-3xl text-zinc-900">My Bookings</h1>
        <p className="text-zinc-600 mt-3 mb-8">Sign in with Google to view and manage all your bookings in one place.</p>
        <Button onClick={login} data-testid="account-google-login" className="gold-gradient text-white font-semibold rounded-full h-12 px-8"><LogIn size={18} className="mr-2" /> Continue with Google</Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-14">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          {user.picture && <img src={user.picture} alt="" className="h-11 w-11 rounded-full border border-gold/30" />}
          <div>
            <h1 className="font-display text-2xl text-zinc-900">Hello, {user.name?.split(" ")[0] || "Guest"}</h1>
            <p className="text-sm text-zinc-500">{user.email}</p>
          </div>
        </div>
        <Button variant="outline" onClick={logout} data-testid="account-logout" className="border-pink-300 text-zinc-700 rounded-full"><LogOut size={15} className="mr-2" /> Logout</Button>
      </div>

      {bookings.length === 0 ? (
        <div className="text-center py-20 text-zinc-500" data-testid="account-empty">No bookings yet. Book a package to see it here.</div>
      ) : (
        <div className="space-y-4">
          {bookings.map((b) => {
            const idx = FLOW.indexOf(b.status);
            const cancelled = b.status === "cancelled";
            return (
              <div key={b.id} className="bg-card border border-pink-200 rounded-2xl p-6" data-testid={`account-booking-${b.booking_number}`}>
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <span className="text-xs text-zinc-500">{t("booking_number")}</span>
                    <div className="text-lg font-display gold-text">{b.booking_number}</div>
                  </div>
                  <Badge variant="outline" className={`capitalize h-fit ${cancelled ? "border-red-500/40 text-red-400" : "border-gold/40 text-gold"}`}>{t(`status_${b.status}`)}</Badge>
                </div>
                <div className="grid sm:grid-cols-3 gap-3 mt-4 text-sm text-zinc-700">
                  <div className="flex items-center gap-2"><Package size={15} className="text-gold" /> {b.package_name_en}</div>
                  <div className="flex items-center gap-2"><CalendarDays size={15} className="text-gold" /> {b.event_date}</div>
                  <div className="flex items-center gap-2"><IndianRupee size={15} className="text-gold" /> {fmtINR(b.package_price_locked)}</div>
                </div>
                <div className="mt-3 text-sm flex flex-wrap gap-4 text-zinc-600">
                  <span>Advance: <span className={b.payment_status === "paid" ? "text-emerald-400" : "text-amber-400"}>{fmtINR(b.advance_amount)} ({b.payment_status})</span></span>
                  <span>Balance: <span className={b.balance_paid ? "text-emerald-400" : "text-zinc-700"}>{fmtINR(b.balance_amount)} {b.balance_paid ? "(paid)" : ""}</span></span>
                </div>

                {!cancelled && (
                  <div className="flex items-center justify-between relative mt-6 max-w-md">
                    {FLOW.map((s, i) => (
                      <div key={s} className="flex flex-col items-center gap-1.5 flex-1 z-10">
                        <div className={`h-3 w-3 rounded-full ${idx >= i ? "bg-gold" : "bg-zinc-600"}`} />
                        <span className={`text-[10px] ${idx >= i ? "text-gold" : "text-zinc-500"}`}>{t(`status_${s}`)}</span>
                      </div>
                    ))}
                    <div className="absolute top-[5px] left-0 right-0 h-px bg-white/10" />
                  </div>
                )}

                {b.invoice && (
                  <div className="mt-4 rounded-xl bg-white border border-pink-200 p-3 text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2 text-zinc-700"><FileText size={14} className="text-gold" /> {b.invoice.invoice_number}</span>
                    <span className="text-zinc-600">Total {fmtINR(b.invoice.total)} · Due {fmtINR(b.invoice.balance_due)}</span>
                  </div>
                )}

                <BalancePay booking={b} contact={user.email} onSubmitted={load} />

                {!cancelled && b.status !== "completed" && (
                  <Button variant="outline" onClick={() => cancel(b.booking_number)} disabled={busy} data-testid={`account-cancel-${b.booking_number}`} className="mt-3 ml-0 sm:ml-3 border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-full">Cancel Booking</Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
